<template>
  <mpf-dialog
    v-model="isWarningModalVisible"
    type="modal"
    title="Your session will end soon"
    :strapline-text="`If you do not do anything in ${countdownHumanText} ${timeLeftUnits}, we will return you to the start and your progress will be lost`"
    :announce-while-open-ms="20000"
  >
    <template #actions>
      <mpf-button
        label="Stay on this page"
        class="mpf-u-margin-0"
        @click="inactivity.stayOnThisPage"
      />
    </template>
  </mpf-dialog>

  <header class="mpf-header" ref="pageHeader" role="banner" data-module="mpf-header">
    <div class="mpf-header__container mpf-width-container">
      <div class="mpf-header__service">
        <!-- TODO: needs WCAG fallback and tenant details-->
        <h1 class="mpf-tenant-tite mpf-u-margin-top-3 mpf-u-margin-bottom-3 mpf-heading-m">
          <span
            v-if="tenantStore.isLoaded && !tenantStore?.tenant"
            class="brand-logo"
            aria-hidden="true"
          ></span>

          <span v-if="tenantStore?.tenant?.tenantId">{{
            tenantStore?.tenant?.localOrganisation?.name || 'NHS England'
          }}</span>
        </h1>
      </div>
    </div>
  </header>
  <div class="mpf-width-container">
    <div v-if="backButton && !hideBackButton" ref="pageBackButton">
      <MpfBackLink text="Back" history-back />
    </div>

    <main class="mpf-main-wrapper" :class="[pageRouteName, pageHasBackButton]" ref="mainWrapper">
      <div v-if="metaOverride.notificationBanner?.text" class="mpf-u-margin-bottom-6">
        <MpfNotificationBanner
          v-if="metaOverride.notificationBanner?.text"
          :heading="metaOverride.notificationBanner.title"
          marginClass="mpf-u-margin-bottom-4"
        >
          <p>{{ metaOverride.notificationBanner.text }}</p>
        </MpfNotificationBanner>
      </div>
      <component :is="layoutComponent">
        <RouterView />
      </component>
    </main>
  </div>
  <div ref="pageFooter">
    <PageFooter />
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
  import { useRoute, RouterView } from 'vue-router';
  import { DefaultLayout } from './lib/layouts/layouts';
  import PageFooter from './lib/components/PageFooter.vue';
  import MpfBackLink from './lib/components/MpfBackLink/MpfBackLink.vue';
  import MpfDialog from './lib/components/MpfDialog/MpfDialog.vue';
  import MpfButton from './lib/components/MpfButton/MpfButton.vue';

  import { useViewportHeightMinus } from './lib/composables/useViewportHeightMinus';
  import { useSessionInactivity } from './lib/composables/useSessionInactivity';
  import { useSessionStore } from './stores/useSessionStore';
  import { router } from './router';
  import { storeToRefs } from 'pinia';
  import { getCountdownUnit } from './lib/helpers/time-format.helper';
  import { useTenantStore } from './stores/useTenantStore';
  import useErrorNavigation from './lib/composables/useErrorNavigation';
  import { useRequestStoresCommon } from './lib/composables/useRequestStoresCommon';
  import { useHideBackButtonFromRoutes } from './lib/composables/useHideBackButtonFromRoutes';
  import MpfNotificationBanner from './lib/components/MpfNotificationBanner/MpfNotificationBanner.vue';
  import { useRouteMetaOverrideStore } from './stores/useRouteMetaOverrideStore';
  import { getTenantFromPath } from './lib/composables/useTenantNavigation';
  const errorNavigation = useErrorNavigation();
  const { endAll } = useRequestStoresCommon();

  const { hideBackButton } = useHideBackButtonFromRoutes(router, ['/nhs-login-callback']);

  const tenantStore = useTenantStore();
  const route = useRoute();

  // Always pick the current route's layoutComponent, fallback to DefaultLayout
  const layoutComponent = computed(() => {
    return (route?.meta.layoutComponent as any) || DefaultLayout;
  });

  const backButton = computed(() => {
    return route?.meta?.backButton;
  });

  const isWarningOpen = ref(false);
  const pageRouteName = computed(() => route.name);

  const pageHasBackButton = computed(() => (route.meta.backButton ? 'has-back-button' : ''));

  const pageHeader = ref<HTMLElement | null>(null);
  const pageBackButton = ref<HTMLElement | null>(null);
  const pageFooter = ref<HTMLElement | null>(null); // now DOM element
  const mainWrapper = ref<HTMLElement | null>(null);

  const VITE_ENV = import.meta.env.VITE_ENV?.toUpperCase() || 'DEVELOPMENT';

  onMounted(async () => {
    const tenantId = getTenantFromPath(route.path);
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') {
      console.info('onMounted Tenant ID from URL:', tenantId);
    }
    await tenantStore.fetchTenant(tenantId);

    // TODO: check vals for UAT & STAGING
    if (import.meta.env.DEV) {
      (window as any).__forceSessionTimeout = (ms: number) => {
        forceSessionTimeout(ms);
      };
      // NB. call from console with window.__forceSessionTimeout(60000 * 9.8)
    }
  });

  watch(
    () => route.fullPath,
    async (p) => {
      // NB. load tenant
      const tenantId = getTenantFromPath(p);
      if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') {
        console.info('watch Tenant ID from URL:', tenantId);
      }
      await tenantStore.fetchTenant(tenantId);
    },
  );

  // session timers
  const session = useSessionStore();
  const sessionStore = storeToRefs(session);
  const { countdownHumanText, isWarningModalVisible } = sessionStore;

  const inactivity = useSessionInactivity({
    store: session,
    router,
    countdownEnabled: true,
    ignoreSelectors: ['.mpf-dialog'],
    sessionEndedPath: '/session/expired',
    closeWarningUi: () => {
      isWarningOpen.value = false;
    },
    clearAppSessionData: async () => {
      await endAll('timeout');
      const result = await session.endSession();
      if (!result.ok) {
        await errorNavigation.navigateToError({
          message: 'Something went wrong',
          errors: result.errors,
        });
        return false;
      }
      return true;
    },
  });

  // NB. so we can call session functions elsewhere
  provide('sessionInactivity', inactivity);

  // NB. for testing session dialog
  const forceSessionTimeout = (ms) => {
    session.startSession(Date.now());
    inactivity.start();
    inactivity.setLastActivityAgo(ms);
  };
  const timeLeftUnits = computed(() => getCountdownUnit(session.countdownWarningMsTick));

  onBeforeUnmount(() => {
    if (import.meta.env.DEV) {
      delete (window as any).__setInactivityLastAgo;
    }
  });

  useViewportHeightMinus({
    main: mainWrapper,
    minus: [
      { label: 'header', ref: pageHeader },
      { label: 'backButton', ref: pageBackButton },
      { label: 'footer', ref: pageFooter },
    ],
    debug: false,
    useDynamicViewport: true,
  });

  const metaOverride = useRouteMetaOverrideStore();

  // TODO: theme tester selecting and switcher
  // document.documentElement.setAttribute('data-theme', 'medicus');
  // document.documentElement.setAttribute('data-theme', 'nhs');
</script>
<style lang="scss">
  // min height stops layout shift whilst waiting for tenant title
  .mpf-header {
    min-height: 77px;
  }

  @media (width >= 40.0625em) {
    .mpf-header {
      min-height: 96px;
    }
  }
</style>
